/**
 * Shared subscription-presentation logic.
 *
 * This is the single source of truth for how the UI labels an organization's
 * subscription. It exists because several components independently (and wrongly)
 * treated `subscriptionTier === 'free'` as "trial expired" — which mislabelled
 * both never-subscribed free orgs AND brand-new trials (which are momentarily
 * free/NULL before the trial state is persisted) as expired.
 *
 * Access decisions (can this org use premium features) still come from the
 * backend (`org.access` computed by middleware.ComputeOrgAccess). This helper is
 * for DISPLAY only.
 */

export type SubscriptionPresentation =
  | 'trialing'
  | 'trialExpired'
  | 'free'
  | 'paid'
  | 'pastDue'
  | 'provisioning'

export interface SubscriptionStateInput {
  subscriptionTier?: string | null
  subscriptionStatus?: string | null
  trialEndsAt?: string | null
  subscriptionEndsAt?: string | null
  /**
   * Set true by a flow that just successfully created a trial but whose
   * refreshed server state is not yet trialing (webhook still in flight). Lets
   * the UI show "activating" instead of momentarily flashing "free"/"expired".
   */
  provisioningRequested?: boolean
}

const isFutureDate = (value?: string | null): boolean => {
  if (!value) return false
  const time = new Date(value).getTime()
  return !Number.isNaN(time) && time > Date.now()
}

const isPastDate = (value?: string | null): boolean => {
  if (!value) return false
  const time = new Date(value).getTime()
  return !Number.isNaN(time) && time <= Date.now()
}

/**
 * Derive the presentation state for an organization's subscription.
 *
 * Ordering matters: a failed payment first, then paid/active, then active
 * trial, then expired trial, then provisioning, then plain free.
 */
export function getSubscriptionPresentation(
  org: SubscriptionStateInput | null | undefined,
): SubscriptionPresentation {
  const tier = org?.subscriptionTier ?? undefined
  const status = org?.subscriptionStatus ?? undefined

  // Past due — a payment problem, NOT a trial expiry. This MUST be checked
  // before the paid branch: a past-due org keeps subscription_tier = 'paid'
  // (only its status changes), so testing tier first made this state
  // unreachable for every real customer and silently hid the payment warning.
  if (status === 'past_due') {
    return 'pastDue'
  }

  // Paid / active.
  if (tier === 'paid' || status === 'active') {
    return 'paid'
  }

  // Active trial: an explicit trialing status, or the trial tier with a trial
  // end still in the future.
  if (status === 'trialing') {
    return 'trialing'
  }
  if (tier === 'trial' && isFutureDate(org?.trialEndsAt)) {
    return 'trialing'
  }

  // Expired trial: a trial end date that has passed, or the trial tier whose
  // status/date indicate it lapsed. A trial tier with no future end date is
  // treated as expired (it had a trial and it is over).
  if (isPastDate(org?.trialEndsAt)) {
    return 'trialExpired'
  }
  if (tier === 'trial') {
    return 'trialExpired'
  }

  // Provisioning: a trial was just requested but the refreshed state has not
  // caught up yet. Only when explicitly signalled — never inferred from `free`.
  if (org?.provisioningRequested) {
    return 'provisioning'
  }

  // Free: never subscribed, no future trial date, no evidence of an expired
  // trial. Critically, `free` on its own does NOT mean "trial expired".
  return 'free'
}

/** Whole days remaining until the trial ends (0 if past or unknown). */
export function trialDaysRemaining(trialEndsAt?: string | null): number {
  if (!trialEndsAt) return 0
  const end = new Date(trialEndsAt).getTime()
  if (Number.isNaN(end)) return 0
  const diffDays = Math.ceil((end - Date.now()) / (1000 * 60 * 60 * 24))
  return Math.max(0, diffDays)
}
